import { TradeItem, TableSection, DisplayRow, TradeStatus, BoPostingState } from '@/types';

/**
 * Statuses that unconditionally demand operator follow-up:
 *  - `pending`  — awaiting BO open.
 *  - `rejected` — BO failed N times; operator must Retry or Cancel (LR-408).
 * `matured` is intentionally NOT here: it's a normal post-active step awaiting
 * its scheduled close, not an exception on its own — LR-673's `staleMaturedDays`
 * below adds the conditional case (no close date, stale for N business days).
 *
 * This list, and ONLY this list, is what the MetricsBar Attention chip counts and
 * filters (LR-686): its click sets the sidebar Status filter to exactly these
 * statuses, so it must stay in step with the backend `ATTENTION_STATUSES` in
 * apps/positions/services/metrics.py, which computes `attention_count`.
 *
 * The conditional row signals — `staleMaturedDays` (LR-673) and
 * `staleRecallDays` (LR-703) below — are deliberately NOT part of it. They mark
 * the row with an amber accent + tooltip but are absent from the chip's count and
 * from its filter, because a counted-but-unfilterable category would make the
 * chip's number larger than what clicking it opens (LR-673 round 2; the backend
 * carries the same note, and the KB says so to the operator). They are FE-only:
 * there is no backend twin to keep in sync since LR-673 round 2 removed
 * `_is_stale_matured`.
 */
export const ATTENTION_STATUSES: readonly TradeStatus[] = ['pending', 'rejected'];

/**
 * Determines if a trade requires attention. Extend ATTENTION_STATUSES to add
 * more criteria in the future.
 */
export function isRequireAttention(item: TradeItem): boolean {
  return ATTENTION_STATUSES.includes(item.status);
}

/**
 * Weekday (Mon-Fri) count strictly after `from` up to and including `to` — no
 * holiday calendar (LR-673 is a soft UI nudge, not an accrual calculation).
 * Both args are 'YYYY-MM-DD' wire dates; parsed at UTC midnight so the count
 * is stable regardless of the viewer's own timezone. Mirrors the backend's
 * `_business_days_since` (apps/positions/services/metrics.py).
 */
function businessDaysSince(from: string, to: string): number {
  const start = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  let days = 0;
  const cursor = new Date(start);
  while (cursor < end) {
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    const dow = cursor.getUTCDay();
    if (dow !== 0 && dow !== 6) days++;
  }
  return days;
}

/**
 * LR-673: a Matured position with no Close Settlement Date is easy to lose
 * track of — `matured` never re-enters ATTENTION_STATUSES on its own, and
 * nothing else prompts for the close date.
 *
 * `today` should be `todayUtcKey()` (utils/format), NOT `todayLocalKey()` —
 * this compares against a serialized backend date the same way the backend's
 * own `timezone.now().date()` (UTC) would (see todayUtcKey's own doc comment).
 *
 * Returns the elapsed business-day count once it reaches `thresholdDays` (so
 * the caller can put the number in a tooltip), else null.
 */
export function staleMaturedDays(
  item: TradeItem,
  thresholdDays: number,
  today: string,
): number | null {
  if (item.status !== 'matured' || item.actual_settlement_date || !item.maturity_date) {
    return null;
  }
  const elapsed = businessDaysSince(item.maturity_date, today);
  return elapsed >= thresholdDays ? elapsed : null;
}

/**
 * LR-703: a partial recall/return whose contractual settlement date has passed
 * while it is still Pending. Settling is a MANUAL operator click by design (spec
 * §6.3.2 — the real return day can differ from the contractual one, and the
 * accrual reprices at the click), so no task chases the date and nothing marks
 * the row: the position reads `Pending Partial Settlement`, a derived status over
 * a stored `active`, which no ATTENTION_STATUS covers. Meanwhile accrual keeps
 * running on the FULL booked quantity (booked only drops on Settle) and the
 * reverse POSITION TRANSFER never reaches the back office — quieter than the
 * stale-Matured case, which at least has an accent.
 *
 * Reads `pending_recall_settlement_date` — the EARLIEST pending recall's date,
 * annotated server-side — so the signal ages with the leg that has waited longest.
 * `today` is `todayUtcKey()`, for the same reason as `staleMaturedDays` above.
 *
 * Returns the elapsed business-day count once it reaches `thresholdDays`, else
 * null. Threshold from `recall_stale_days_threshold` (/settings/).
 */
export function staleRecallDays(
  item: TradeItem,
  thresholdDays: number,
  today: string,
): number | null {
  // No date → nothing to be overdue against (a pending recall may carry none).
  if (!item.pending_recall_settlement_date) return null;
  // Terminal rows are out: a closed/cancelled trade's leftover pending recall is
  // not an operator action any more, and History would light up with old rows.
  if (item.status === 'closed' || item.status === 'cancelled') return null;
  const elapsed = businessDaysSince(item.pending_recall_settlement_date, today);
  return elapsed >= thresholdDays ? elapsed : null;
}


/**
 * LR-711: a BackOffice posting on this trade that never resolved.
 *
 * The third conditional row signal, and the quietest class of stall yet. A posting
 * that timed out leaves the trade sitting in its own status indefinitely — a
 * `pending_open_settlement` stuck on `transfer_open` is not in ATTENTION_STATUSES,
 * carries no overdue date, and until LR-711 was visible only in Django-admin.
 * TRD-111 sat that way for 25 days with nothing in the blotter to find it by.
 *
 * Unconditional, unlike its two siblings: there is no threshold to wait out.
 * `staleMaturedDays` and `staleRecallDays` age a NORMAL state into a suspicious one
 * (a matured trade is fine until it is old), whereas the backend only ever sends
 * `bo_posting` for a posting that already failed or is already lost — the waiting
 * happened before the row was written.
 *
 * Terminal trades are excluded for the same reason as the recall signal: a
 * closed/cancelled trade's leftover posting is nobody's action any more, and
 * History would light up with old rows.
 *
 * FE-only, like the other two: it marks the row, and is deliberately absent from
 * the MetricsBar chip's count and filter — see ATTENTION_STATUSES above for why.
 */
export function stuckBoPosting(item: TradeItem): BoPostingState | null {
  const posting = item.bo_posting;
  if (!posting) return null;
  if (item.status === 'closed' || item.status === 'cancelled') return null;
  return posting;
}

/**
 * How the stuck posting reads in the row tooltip.
 *
 * LR-712 moved the wording itself to `utils/trades/bo-posting-copy`, which is now
 * the single source for every sentence about a posting — the panel on the trade
 * page had a second dictionary for the same four states. Re-exported here because
 * this is where the row's other two signals live and where its callers look.
 */
export { boPostingHeadline as stuckBoPostingTitle } from './bo-posting-copy';

/**
 * Builds the table sections for a flat list of trades.
 *
 * LR-686: the book is ONE headerless section. It used to be split into a
 * "REQUIRE ATTENTION" bucket (pending / rejected) above the open book, and
 * since LR-263 the open book had no header of its own — so under a user sort
 * the boundary was invisible and the list read as mis-sorted (110 … 74, then
 * 112, 111). A user sort is now monotonic across the whole book; attention
 * rows stay findable through the MetricsBar "Attention" chip (count + quick
 * filter) and the amber row accent (isRequireAttention + staleMaturedDays).
 * Zero trades yields zero sections (the table renders its empty-state instead).
 */
export function buildSections(items: TradeItem[]): TableSection[] {
  if (items.length === 0) return [];
  return [{ id: 'book', title: '', items }];
}

/**
 * Builds a flat display row array from sections,
 * interleaving section headers with trade rows. A section with an empty
 * `title` renders its items without a header row — the convention the live
 * book and the History view both use for a plain, ungrouped list.
 */
export function buildDisplayRows(sections: TableSection[]): DisplayRow[] {
  const rows: DisplayRow[] = [];
  for (const section of sections) {
    if (section.title !== '') {
      rows.push({
        type: 'section-header',
        sectionId: section.id,
        title: section.title,
        count: section.items.length,
      });
    }
    for (const item of section.items) {
      rows.push({ type: 'trade', item });
    }
  }
  return rows;
}
